import { t } from 'i18next'
import { useEffect, useRef } from 'react'

import { SerializedError } from '@reduxjs/toolkit'
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query'

import { displaySnackBar } from '../features/snackbar'
import { SnackBarMessage } from '../features/snackbar/types/SnackBarMessage'
import { useAppDispatch } from './storeHooks'

type UseErrorHookParams = {
  error?: FetchBaseQueryError | SerializedError
  message?: SnackBarMessage
}

const AbortError = 'AbortError'

/**
 * A hook for using errors received from redux toolkit query hooks and displaying a snackbar message
 */
const messageDefaults: SnackBarMessage = {
  message: t('common:general_error_message'),
  severity: 'error',
  open: true,
}

export const useError = ({ error, message }: UseErrorHookParams) => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (error && (error as SerializedError)?.name !== AbortError) {
      dispatch(displaySnackBar({ ...messageDefaults, ...message }))
    }
  }, [dispatch, message, error])
}

type UseErrorOnceHookParams = UseErrorHookParams & {
  // Identifies what the error belongs to; each distinct key is reported at most once.
  reportKey: string
}

/**
 * Like `useError`, but reports at most one snackbar per `reportKey`. For queries that several components
 * subscribe to, where RTK Query re-requests a failed entry as subscribers arrive and every
 * retry hands back a fresh error object for the same failure.
 */
export const useErrorOnce = ({ error, message, reportKey }: UseErrorOnceHookParams) => {
  const dispatch = useAppDispatch()
  const reportedKey = useRef<string>()

  useEffect(() => {
    if (error && (error as SerializedError)?.name !== AbortError && reportedKey.current !== reportKey) {
      reportedKey.current = reportKey
      dispatch(displaySnackBar({ ...messageDefaults, ...message }))
    }
  }, [dispatch, message, error, reportKey])
}
